import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Fade } from '@mui/material';
import { VisibilityOutlined as Visibility, VisibilityOffOutlined as VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { register, clearError, clearFieldErrors, clearSuccess } from '../store/slices/authSlice';
import LightThemeWrapper from '../components/LightThemeWrapper';
import PublicNavbar from '../components/PublicNavbar';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import { PasswordValidator } from '../utils/passwordValidation';


const Register: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMismatchError, setPasswordMismatchError] = useState('');
  const [touched, setTouched] = useState({
    email: false,
    username: false,
    password: false,
    confirmPassword: false
  });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Explicitly type the auth state to avoid 'unknown' property errors
  interface ValidationErrors {
    [field: string]: string[];
  }

  interface AuthState {
    loading: boolean;
    error: string | null;
    fieldErrors: ValidationErrors | null;
    successMessage: string | null;
    isAuthenticated: boolean;
  }

  const { loading, error, fieldErrors, successMessage, isAuthenticated } = useAppSelector(
    (state: { auth: AuthState }) => state.auth
  );

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      if (successMessage) {
        // Show success message for 2 seconds before redirecting
        const timer = setTimeout(() => {
          navigate('/getting-started');
        }, 2000);
        return () => clearTimeout(timer);
      } else {
        // Already authenticated, redirect immediately
        navigate('/getting-started');
      }
    } else if (successMessage && !isAuthenticated) {
      // Registration successful but not auto-logged in, redirect to login
      const timer = setTimeout(() => {
        navigate('/login');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, successMessage, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSuccess());
    };
  }, [dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      email: true,
      username: true,
      password: true,
      confirmPassword: true
    });

    // Client-side password validation
    const passwordValidation = PasswordValidator.validatePassword(password);
    if (!passwordValidation.isValid) {
      // Password validation errors will be shown by the PasswordStrengthIndicator
      return;
    }

    // Check password confirmation
    if (password !== confirmPassword) {
      setPasswordMismatchError('Passwords do not match. Please ensure both password fields are identical.');
      return;
    }

    // Clear password mismatch error if passwords match
    setPasswordMismatchError('');

    // Clear any existing field errors before submitting
    dispatch(clearFieldErrors());
    dispatch(register({ email, username, password }));
  };

  // Handle field blur events to show validation errors
  const handleFieldBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  // Handle password confirmation change
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    
    // Clear mismatch error when user starts typing
    if (passwordMismatchError) {
      setPasswordMismatchError('');
    }
    
    // Show mismatch error if passwords don't match and field is not empty
    if (value && password && value !== password && touched.confirmPassword) {
      setPasswordMismatchError('Passwords do not match');
    }
  };

  // Handle password change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    
    // Check if confirm password needs to be updated
    if (confirmPassword && value !== confirmPassword && touched.confirmPassword) {
      setPasswordMismatchError('Passwords do not match');
    } else if (confirmPassword && value === confirmPassword) {
      setPasswordMismatchError('');
    }
  };

  return (
    <LightThemeWrapper>
      <PublicNavbar />
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        pt: 8,
        px: isMobile ? 2 : 3
      }}>
        <Fade in timeout={600}>
          <Container maxWidth="xs">
        <Paper elevation={1} sx={{ 
          p: isMobile ? 2 : 3, 
          maxWidth: isMobile ? '100%' : 400, 
          mx: 'auto',
          borderRadius: isMobile ? 2 : 3
        }}>
          <Box sx={{ textAlign: 'center', mb: isMobile ? 2 : 3 }}>
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                mb: isMobile ? 0.5 : 1, 
                color: '#000',
                fontSize: isMobile ? '1.25rem' : '1.5rem'
              }}
            >
              Create Account
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
            >
              Join AGRANA to analyze your emails with AI
            </Typography>
          </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => handleFieldBlur('email')}
            size="small"
            error={!!(fieldErrors?.email && touched.email)}
            helperText={(fieldErrors?.email && touched.email) ? fieldErrors.email[0] : ''}
            sx={{ mb: isMobile ? 1 : 1.5 }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onBlur={() => handleFieldBlur('username')}
            size="small"
            error={!!(fieldErrors?.username && touched.username)}
            helperText={(fieldErrors?.username && touched.username) ? fieldErrors.username[0] : ''}
            sx={{ mb: isMobile ? 1 : 1.5 }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="new-password"
            value={password}
            onChange={handlePasswordChange}
            onBlur={() => handleFieldBlur('password')}
            size="small"
            error={!!(fieldErrors?.password && touched.password)}
            helperText={(fieldErrors?.password && touched.password) ? fieldErrors.password[0] : ''}
            sx={{ mb: isMobile ? 1 : 1.5 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <PasswordStrengthIndicator password={password} />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            onBlur={() => handleFieldBlur('confirmPassword')}
            size="small"
            error={!!passwordMismatchError}
            helperText={passwordMismatchError || ''}
            sx={{ mb: isMobile ? 2 : 2.5 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size={isMobile ? "small" : "medium"}
            disabled={loading || !PasswordValidator.validatePassword(password).isValid || password !== confirmPassword}
            sx={{
              mt: isMobile ? 1.5 : 2,
              mb: isMobile ? 1.5 : 2,
              py: isMobile ? 1 : 1.2,
              fontSize: isMobile ? '0.8rem' : '0.875rem',
              backgroundColor: '#a31830',
              '&:hover': {
                backgroundColor: '#8b1426',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Create Account'
            )}
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', mt: isMobile ? 1.5 : 2 }}>
          <Typography 
            variant="body2" 
            sx={{ fontSize: isMobile ? '0.75rem' : '0.8rem' }}
          >
            Already have an account?{' '}
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                color: '#a31830',
                textDecoration: 'none',
                fontWeight: 400,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Sign in
            </Link>
          </Typography>
        </Box>
        </Paper>
          </Container>
        </Fade>
      </Box>
    </LightThemeWrapper>
  );
};

export default Register;